/**
 * Canva Social Media Link Detector
 *
 * Detects social media links in Canva HTML exports and provides
 * utilities for auto-populating from business profile and replacement.
 */

/**
 * Social media link structure for business profile storage
 */
export interface SocialMediaLinks {
  facebook_url?: string | null;
  instagram_url?: string | null;
  twitter_url?: string | null;
  linkedin_url?: string | null;
  youtube_url?: string | null;
}

/**
 * Detected social link from Canva HTML
 */
export interface DetectedSocialLink {
  platform: keyof SocialMediaLinks;
  url: string;
  originalHref: string;
  /** True if detected as text mention (not an actual link), needs to be wrapped in anchor */
  isTextMention?: boolean;
  /** The element selector or identifier for text mentions */
  textSelector?: string;
  /** The actual text that was detected (for display in UI) */
  detectedText?: string;
}

/**
 * Regex patterns for detecting social media platform URLs
 */
const SOCIAL_PATTERNS: Record<keyof SocialMediaLinks, RegExp> = {
  facebook_url: /(?:https?:\/\/)?(?:www\.)?(?:facebook\.com|fb\.com|fb\.me)\/[^\s"'<>]+/i,
  instagram_url: /(?:https?:\/\/)?(?:www\.)?instagram\.com\/[^\s"'<>]+/i,
  twitter_url: /(?:https?:\/\/)?(?:www\.)?(?:twitter\.com|x\.com)\/[^\s"'<>]+/i,
  linkedin_url: /(?:https?:\/\/)?(?:www\.)?linkedin\.com\/[^\s"'<>]+/i,
  youtube_url: /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com|youtu\.be)\/[^\s"'<>]+/i,
};

/**
 * Patterns for detecting social media platform mentions as text
 * Used to find text like "Instagram", "Facebook" that could be converted to links
 */
const SOCIAL_TEXT_PATTERNS: Record<keyof SocialMediaLinks, RegExp> = {
  facebook_url: /\bfacebook\b/i,
  instagram_url: /\binstagram\b/i,
  twitter_url: /\b(?:twitter|x\.com)\b/i,
  linkedin_url: /\blinkedin\b/i,
  youtube_url: /\byoutube\b/i,
};

/**
 * Platform display names for UI
 */
export const SOCIAL_PLATFORM_NAMES: Record<keyof SocialMediaLinks, string> = {
  facebook_url: 'Facebook',
  instagram_url: 'Instagram',
  twitter_url: 'Twitter/X',
  linkedin_url: 'LinkedIn',
  youtube_url: 'YouTube',
};

/**
 * Detect social media links in Canva HTML
 *
 * Searches for:
 * 1. Anchor elements with hrefs matching social media patterns
 * 2. Text elements containing social media platform names (e.g., "Instagram", "Facebook")
 *
 * Returns deduplicated list of detected platforms with their original URLs or text.
 */
export function detectSocialLinks(html: string): DetectedSocialLink[] {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  const detected: DetectedSocialLink[] = [];
  const seenPlatforms = new Set<string>();

  // First, check for actual anchor tags with social media URLs
  const anchorElements = doc.querySelectorAll('a[href]');

  anchorElements.forEach((anchor) => {
    const href = anchor.getAttribute('href') || '';
    const anchorText = anchor.textContent?.trim() || '';

    // Check against each social platform pattern
    for (const [platform, pattern] of Object.entries(SOCIAL_PATTERNS) as [keyof SocialMediaLinks, RegExp][]) {
      if (pattern.test(href)) {
        // Only add first occurrence of each platform
        if (!seenPlatforms.has(platform)) {
          detected.push({
            platform,
            url: href,
            originalHref: href,
            isTextMention: false,
            detectedText: anchorText || href.substring(0, 40), // Show anchor text or truncated URL
          });
          seenPlatforms.add(platform);
        }
        break; // Only match one platform per URL
      }
    }
  });

  // Second, check for text mentions of social platforms (not wrapped in links)
  // This catches Canva templates with "Instagram", "Facebook" as styled text
  const textWalker = doc.createTreeWalker(
    doc.body || doc.documentElement,
    NodeFilter.SHOW_TEXT,
    null
  );

  let textNode: Text | null;
  while ((textNode = textWalker.nextNode() as Text | null)) {
    const text = textNode.textContent || '';
    const parent = textNode.parentElement;

    // Skip if already inside a link or if parent doesn't exist
    if (!parent || parent.closest('a')) continue;

    for (const [platform, pattern] of Object.entries(SOCIAL_TEXT_PATTERNS) as [keyof SocialMediaLinks, RegExp][]) {
      if (pattern.test(text) && !seenPlatforms.has(platform)) {
        // Generate a selector-like identifier for this text node
        const tagName = parent.tagName.toLowerCase();
        const textContent = text.trim().substring(0, 30);
        // Extract the matched text for display
        const match = text.match(pattern);
        const matchedText = match ? match[0] : textContent;

        detected.push({
          platform,
          url: '', // No URL yet - will be filled from business profile
          originalHref: '',
          isTextMention: true,
          textSelector: `${tagName}:contains("${textContent}")`,
          detectedText: matchedText, // The actual text that was found
        });
        seenPlatforms.add(platform);
        break;
      }
    }
  }

  return detected;
}

/**
 * Merge detected social links with business profile links
 *
 * Priority: business profile links override detected links (since user can edit)
 * If business profile has no link for a platform, use detected link
 */
export function mergeWithBusinessLinks(
  detectedLinks: DetectedSocialLink[],
  businessLinks: SocialMediaLinks
): SocialMediaLinks {
  const result: SocialMediaLinks = {};

  // Start with detected links
  for (const { platform, url } of detectedLinks) {
    result[platform] = url;
  }

  // Override with business profile links (if set)
  for (const platform of Object.keys(SOCIAL_PATTERNS) as (keyof SocialMediaLinks)[]) {
    if (businessLinks[platform]) {
      result[platform] = businessLinks[platform];
    }
  }

  return result;
}

/**
 * Replace social media links in HTML with new URLs
 *
 * Finds all anchors matching social patterns and replaces their hrefs
 * with the corresponding URL from the socialLinks object.
 * Also wraps text mentions of social platforms in anchor tags if URL is provided.
 */
export function replaceSocialLinksInHtml(
  html: string,
  socialLinks: SocialMediaLinks
): string {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  let modified = false;

  // First, update existing anchor tags
  const anchorElements = doc.querySelectorAll('a[href]');

  anchorElements.forEach((anchor) => {
    const href = anchor.getAttribute('href') || '';

    // Check which platform this link belongs to
    for (const [platform, pattern] of Object.entries(SOCIAL_PATTERNS) as [keyof SocialMediaLinks, RegExp][]) {
      if (pattern.test(href)) {
        const newUrl = socialLinks[platform];
        if (newUrl && newUrl !== href) {
          anchor.setAttribute('href', newUrl);
          modified = true;
        }
        break;
      }
    }
  });

  // Second, wrap text mentions in anchor tags
  const textWalker = doc.createTreeWalker(
    doc.body || doc.documentElement,
    NodeFilter.SHOW_TEXT,
    null
  );

  const nodesToWrap: { node: Text; platform: keyof SocialMediaLinks; url: string }[] = [];

  let textNode: Text | null;
  while ((textNode = textWalker.nextNode() as Text | null)) {
    const text = textNode.textContent || '';
    const parent = textNode.parentElement;

    // Skip if already inside a link or no parent
    if (!parent || parent.closest('a')) continue;

    for (const [platform, pattern] of Object.entries(SOCIAL_TEXT_PATTERNS) as [keyof SocialMediaLinks, RegExp][]) {
      const url = socialLinks[platform];
      if (pattern.test(text) && url) {
        // Collect nodes to wrap (can't modify while walking)
        nodesToWrap.push({ node: textNode, platform, url });
        break;
      }
    }
  }

  // Now wrap collected text nodes
  for (const { node, platform, url } of nodesToWrap) {
    const parent = node.parentElement;
    if (!parent) continue;

    const text = node.textContent || '';
    const pattern = SOCIAL_TEXT_PATTERNS[platform];
    const match = text.match(pattern);

    if (match && match.index !== undefined) {
      // Create document fragment with: before text + anchor + after text
      const frag = doc.createDocumentFragment();
      const beforeText = text.substring(0, match.index);
      const matchedText = match[0];
      const afterText = text.substring(match.index + matchedText.length);

      if (beforeText) {
        frag.appendChild(doc.createTextNode(beforeText));
      }

      const anchor = doc.createElement('a');
      anchor.href = url;
      anchor.textContent = matchedText;
      // Preserve parent's styles by copying inline style if it exists
      if (parent.style.cssText) {
        anchor.style.cssText = parent.style.cssText;
      }
      anchor.style.color = 'inherit';
      anchor.style.textDecoration = 'underline';
      frag.appendChild(anchor);

      if (afterText) {
        frag.appendChild(doc.createTextNode(afterText));
      }

      node.replaceWith(frag);
      modified = true;
    }
  }

  // If no modifications, return original to preserve formatting
  if (!modified) return html;

  return doc.documentElement.outerHTML;
}

/**
 * Check if HTML contains any detectable social media links or text mentions
 */
export function hasSocialLinks(html: string): boolean {
  // Check for URL patterns
  for (const pattern of Object.values(SOCIAL_PATTERNS)) {
    if (pattern.test(html)) {
      return true;
    }
  }
  // Check for text mentions
  for (const pattern of Object.values(SOCIAL_TEXT_PATTERNS)) {
    if (pattern.test(html)) {
      return true;
    }
  }
  return false;
}

/**
 * Get empty social links object
 */
export function getEmptySocialLinks(): SocialMediaLinks {
  return {
    facebook_url: null,
    instagram_url: null,
    twitter_url: null,
    linkedin_url: null,
    youtube_url: null,
  };
}
